import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, FileText, Map, ShoppingCart, Flame, AlertTriangle } from 'lucide-react';
import { T, glassLight, useIsMobile } from '../ui';
import { useGasData } from './gasData';
import type { GasView } from '../types';
import { Clientes } from './clientes';
import { Contratos } from './Contratos';
import { Rutas } from './Rutas';
import { Ventas } from './Ventas';
const NAV: {
  id: GasView;
  label: string;
  icono: React.ElementType;
}[] = [{
  id: 'clientes',
  label: 'Clientes',
  icono: Users
}, {
  id: 'contratos',
  label: 'Contratos',
  icono: FileText
}, {
  id: 'rutas',
  label: 'Rutas',
  icono: Map
}, {
  id: 'ventas',
  label: 'Ventas',
  icono: ShoppingCart
}];
function fmtRD(n: number) {
  return new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    maximumFractionDigits: 0
  }).format(n);
}
export default function GasApp() {
  const isMobile = useIsMobile();
  const [view, setView] = useState<GasView>('clientes');
  const data = useGasData();
  const stats = useMemo(() => {
    const hoy = new Date();
    const ventasMes = data.ventas.filter(v => {
      const f = new Date(v.fecha);
      return f.getMonth() === hoy.getMonth() && f.getFullYear() === hoy.getFullYear();
    });
    return {
      clientesActivos: data.clientes.filter(c => c.estado === 'activo').length,
      contratosVigentes: data.contratos.filter(c => c.estado === 'vigente').length,
      rutasActivas: data.rutas.filter(r => r.estado === 'activa').length,
      totalMes: ventasMes.reduce((acc, v) => acc + (v.total || 0), 0)
    };
  }, [data.clientes, data.contratos, data.rutas, data.ventas]);

  // Conteo por pestaña: saber cuántos registros hay detrás de cada una antes
  // de entrar ahorra un clic.
  const conteos: Record<GasView, number> = {
    clientes: data.clientes.length,
    contratos: data.contratos.length,
    rutas: data.rutas.length,
    ventas: data.ventas.length
  };
  const pages: Record<GasView, React.ReactNode> = {
    clientes: <Clientes clientes={data.clientes} onAddCliente={data.onAddCliente} onDeleteCliente={data.onDeleteCliente} onUpdateCliente={data.onUpdateCliente} data-id="element-574" data-mp="155xv5emllvdg" />,
    contratos: <Contratos contratos={data.contratos} clientes={data.clientes} onAddContrato={data.onAddContrato} onDeleteContrato={data.onDeleteContrato} onUpdateContrato={data.onUpdateContrato} data-id="element-575" data-mp="1e6maccnqa1rx" />,
    rutas: <Rutas rutas={data.rutas} clientes={data.clientes} onAddRuta={data.onAddRuta} onDeleteRuta={data.onDeleteRuta} onUpdateRuta={data.onUpdateRuta} data-id="element-576" data-mp="1nv462fh5hiop" />,
    ventas: <Ventas ventas={data.ventas} clientes={data.clientes} onAddVenta={data.onAddVenta} onDeleteVenta={data.onDeleteVenta} onUpdateVenta={data.onUpdateVenta} data-id="element-577" data-mp="1xu3crsicv492" />
  };
  return <div data-id="element-578" data-mp="3hdllijdg95d">
      <style data-id="element-579" data-mp="3hna11orq88y">{`
        .gas-tab { position: relative; z-index: 1; }
        .gas-tab:hover { background: ${T.surfaceHover}; }
      `}</style>

      {/* ── ENCABEZADO ────────────────────────────────────────────────
          Antes era un bloque oscuro con degradados: destacaba, pero era el
          único trozo oscuro de toda la app y parecía de otro sistema. Ahora
          usa la misma paleta clara y las mismas tarjetas de indicador que
          Pedidos, Auditoría y Sucursales. */}
      <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.35,
      ease: [0.2, 0.8, 0.2, 1]
    }} style={{
      marginBottom: 22
    }} data-id="element-580" data-mp="35t0tsl3nz1zl">

        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        marginBottom: 12
      }} data-id="element-581" data-mp="2wufc0s60nqzv">

          <div style={{
          width: 34,
          height: 34,
          borderRadius: 11,
          flexShrink: 0,
          background: 'linear-gradient(135deg, #FF8C00 0%, #FF5A00 100%)',
          boxShadow: '0 5px 16px rgba(255,107,1,.32)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} data-id="element-582" data-mp="v20ptgwfw0dn">

            <Flame size={17} color="#fff" strokeWidth={2.4} data-id="element-583" data-mp="2m59h58dz39de" />
          </div>
          <div style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: T.accent
        }} data-id="element-584" data-mp="2i47iotrvr1ch">

            Gestión comercial
          </div>
        </div>

        <h1 style={{
        fontSize: isMobile ? 27 : 34,
        fontWeight: 800,
        letterSpacing: '-0.03em',
        color: T.text,
        margin: '0 0 6px'
      }} data-id="element-585" data-mp="28ckyw91h7ty4">

          Gas Industrial
        </h1>
        <p style={{
        fontSize: 13.5,
        color: T.textSecondary,
        margin: 0,
        maxWidth: 560
      }} data-id="element-586" data-mp="1udl9v0a1xd1f">

          Clientes, contratos, rutas de distribución y ventas, todo en un solo
          lugar.
        </p>
      </motion.div>

      {/* Resumen transversal. Va en una sola tira compacta, no en tarjetas
          grandes, porque cada pestaña ya trae sus propios indicadores: dos
          filas de tarjetas seguidas competían entre sí. */}
      <div style={{
      ...glassLight,
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
      gap: 0,
      padding: 0,
      marginBottom: 22,
      overflow: 'hidden'
    }} data-id="element-587" data-mp="iw7kc21gj6ri">

        {[{
        label: 'Clientes activos',
        valor: String(stats.clientesActivos),
        color: T.accent
      }, {
        label: 'Contratos vigentes',
        valor: String(stats.contratosVigentes),
        color: T.ok
      }, {
        label: 'Rutas activas',
        valor: String(stats.rutasActivas),
        color: T.info
      }, {
        label: 'Ventas del mes',
        valor: fmtRD(stats.totalMes),
        color: T.warn
      }].map((m, i) => <div key={m.label} style={{
        padding: isMobile ? '14px 16px' : '16px 20px',
        borderLeft: !isMobile && i > 0 ? `1px solid ${T.border}` : undefined,
        borderTop: isMobile && i > 1 ? `1px solid ${T.border}` : undefined,
        borderLeftWidth: isMobile && i % 2 ? 1 : undefined,
        borderLeftStyle: isMobile && i % 2 ? 'solid' : undefined,
        borderLeftColor: isMobile && i % 2 ? T.border : undefined
      }} data-id="element-588" data-mp="ashjpeb18w46" data-mpi={i}>

            <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '.09em',
          textTransform: 'uppercase',
          color: T.textMuted,
          marginBottom: 7
        }} data-id="element-589" data-mp="3j0znoos3oh61">

              <span style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: m.color,
            flexShrink: 0
          }} data-id="element-590" data-mp="1c85ncjqf3f0s" />

              {m.label}
            </div>
            <div style={{
          fontSize: isMobile ? 19 : 23,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: T.text
        }} data-id="element-591" data-mp="1jtrkx7v9ykbc">

              {m.valor}
            </div>
          </div>)}
      </div>

      {data.sinTablas ? <div style={{
      display: 'flex',
      gap: 11,
      alignItems: 'flex-start',
      padding: '13px 16px',
      marginBottom: 20,
      borderRadius: T.rMd,
      background: T.warnSoft,
      border: `1px solid ${T.warn}33`,
      color: T.warn,
      fontSize: 12.5,
      lineHeight: 1.5
    }} data-id="element-592" data-mp="1yi5nrisoy8rw">

          <AlertTriangle size={16} style={{
        flexShrink: 0,
        marginTop: 1
      }} data-id="element-593" data-mp="2hh9rqd5dlccp" />
          <span data-id="element-594" data-mp="2hf6vhbdmqmzj">
            <strong data-id="element-595" data-mp="2m7zzty56rtb3">Solo guardando en este navegador.</strong> Las tablas de
            Gas Industrial todavía no existen en Supabase, así que estos
            registros no se están sincronizando. Corre el script de creación de
            tablas en el SQL Editor de tu proyecto.
          </span>
        </div> : null}

      {/* ── PESTAÑAS ── */}
      <div style={{
      display: 'flex',
      gap: 7,
      marginBottom: 24,
      overflowX: 'auto',
      paddingBottom: 2
    }} data-id="element-596" data-mp="cgql0ah2i161">

        {NAV.map(item => {
        const Icono = item.icono;
        const activa = view === item.id;
        return <button key={item.id} type="button" className="gas-tab" onClick={() => setView(item.id)} style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          borderRadius: 99,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          color: activa ? T.accent : T.textSecondary,
          background: activa ? T.accentSoft : 'transparent',
          border: `1px solid ${activa ? T.accentBorder : T.border}`,
          transition: 'color .15s, border-color .15s'
        }} data-id="element-597" data-mp="1061h3enh37du">

              <Icono size={15} data-id="element-598" data-mp="2a3hzamfdydhn" />
              {item.label}
              <span style={{
            fontSize: 11,
            fontWeight: 800,
            padding: '1px 7px',
            borderRadius: 99,
            background: activa ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
            color: activa ? T.accent : T.textMuted
          }} data-id="element-599" data-mp="2a2zswwz0eiw0">

                {conteos[item.id]}
              </span>
            </button>;
      })}
      </div>

      {/* ── CONTENIDO ── */}
      <div style={{
      ...glassLight,
      padding: isMobile ? 16 : 22
    }} data-id="element-600" data-mp="tpjgrjchwbup">
        <AnimatePresence mode="wait" data-id="element-601" data-mp="1ewceafx98mfy">
          <motion.div key={view} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2,
          ease: [0.2, 0.8, 0.2, 1]
        }} data-id="element-602" data-mp="1exdt8vhe5t2y">

            {pages[view]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}